import React, {useState, createContext, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {TableContainer, TableStyle, TableColumnWidth, TableColumnAlignment} from './Table.styled';
import {TableHeaderGrouping, TableHeaderColumn} from './';

export const LayoutContext = createContext();
export const Table = React.memo(function Table({children, onSort, initialSort, cy}) {
	// use instance variable to keep track of when first update has occured
	const firstUpdate = useRef(true);
	const [sort, setSort] = useState(initialSort ? initialSort : {id: null, direction: null});

	const handleSortClick = id => {
		const newSorting = {...sort};
		if (id === sort.id) {
			if (sort.direction === 'ASC') {
				newSorting.direction = 'DESC';
			}
			if (sort.direction === 'DESC') {
				newSorting.direction = null;
				newSorting.id = null;
			}
		} else {
			newSorting.id = id;
			newSorting.direction = 'ASC';
		}
		setSort(newSorting);
	};

	useEffect(() => {
		// We should not fire sort when first update
		// unless we have an initial sort state
		if (firstUpdate.current && !initialSort) {
			firstUpdate.current = false;
			return;
		}
		if (onSort) {
			onSort(sort);
		}
	}, [sort]);

	const parseTableHeader = tableHeader => {
		const layout = {
			hasGroupings: false,
			columns: [],
			groupings: [],
			minGroupingWidths: [],
			sort,
			onSort: handleSortClick,
		};

		/**
		 * Helper variablies to keep trakck of which group and column the loop has gotten too.
		 * A seperate index needs to be tracked, because ONE child of tableHeader can be a columns
		 * or a grouping of columns
		 */
		let groupIndex = 0;
		let columnIndex = 0;
		let minGroupingWidthIndex = 0;

		if (tableHeader) {
			React.Children.forEach(tableHeader.props.children, headerChild => {
				if (headerChild.type === TableHeaderGrouping) {
					layout.hasGroupings = true;
					/*
            When child type is TableHeaderGrouping, we get the columns one level lower
          */
					const columnLayouts = React.Children.map(headerChild.props.children, groupingChild => {
						layout.hasGroupings = true;
						layout.groupings[columnIndex++] = groupIndex;
						layout.minGroupingWidths[minGroupingWidthIndex++] = headerChild.props.minWidth;
						return {
							width: groupingChild.props.width,
							align: groupingChild.props.align,
							visible: groupingChild.props.visible,
							usePadding: groupingChild.props.usePadding,
							background: groupingChild.props.background,
							flex: groupingChild.props.flex,
						};
					});
					groupIndex++;
					layout.columns.push(...columnLayouts);
				} else if (headerChild.type === TableHeaderColumn) {
					layout.columns.push({
						width: headerChild.props.width,
						align: headerChild.props.align,
						visible: headerChild.props.visible,
						bordered: headerChild.props.bordered,
						usePadding: headerChild.props.usePadding,
						background: headerChild.props.background,
						flex: headerChild.props.flex,
					});
					layout.groupings[columnIndex++] = null;
					layout.minGroupingWidths[minGroupingWidthIndex++] = null;
				} else {
					// This should be checked in proptypes, but still we should never get here.
					throw new Error('Invalid child supplied to table header');
				}
			});
		}

		// cannot use borders if groupings is set
		if (layout.hasGroupings) {
			return layout;
		}

		let lastVisibleColumn = null;
		layout.columns.forEach((column, index, columns) => {
			// first one cannot have borders
			if (index === 0) return;
			if (column.visible) {
				if (column.bordered) {
					column.borderLeft = true;
					// last column so should have both borders;
					if (index === columns.length - 1) {
						column.borderRight = false;
					}
				}
				if (lastVisibleColumn && lastVisibleColumn.bordered) {
					column.borderLeft = true;
				}
				lastVisibleColumn = column;
			}
		});
		return layout;
	};

	return (
		<TableContainer data-cy={cy}>
			<TableStyle>
				<LayoutContext.Provider value={parseTableHeader(children[0])}>{children}</LayoutContext.Provider>
			</TableStyle>
		</TableContainer>
	);
});

Table.TABLE_COLUMN_WIDTH = TableColumnWidth;
Table.TABLE_COLUMN_ALIGNMENT = TableColumnAlignment;
// TODO: Custom validator for checking that Table only has a Table header or Footer
Table.propTypes = {
	children: PropTypes.arrayOf(PropTypes.element).isRequired,
	onSort: PropTypes.func,
	initialSort: PropTypes.object,
	cy: PropTypes.string,
	minGroupingWidth: PropTypes.number,
};
